import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { 
  Loader2, 
  RefreshCw, 
  Calendar,
  Clock,
  TrendingUp,
  Download,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  FileText,
  Package,
  ShoppingCart
} from 'lucide-react';
import { toast } from 'sonner';
import { materialPlanningApiService, MaterialPlanningDto } from '../services/materialPlanningApi';
import { useBranch } from '../contexts/BranchContext';
import { useAuth } from '../contexts/AuthContext';
import { getMaterialPlanningConfig } from '../config/uiConfig';

export function MaterialPlanningPage() {
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();
  const { user } = useAuth();
  const materialPlanningConfig = getMaterialPlanningConfig();
  
  // Calculate default month based on cutoff date
  const getDefaultMonth = () => {
    const now = new Date();
    const currentDate = now.getDate();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();
    
    // If current date is >= cutoff date, show next month
    if (currentDate >= materialPlanningConfig.cutoffDate) {
      // If it's December, go to January of next year
      if (currentMonth === 12) {
        return { year: currentYear + 1, month: 1 };
      } else {
        return { year: currentYear, month: currentMonth + 1 };
      }
    } else {
      // Otherwise show current month
      return { year: currentYear, month: currentMonth };
    }
  };

  const defaultMonth = getDefaultMonth();
  
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number>(defaultMonth.year);
  const [selectedMonth, setSelectedMonth] = useState<number>(defaultMonth.month);
  const [planningData, setPlanningData] = useState<MaterialPlanningDto[]>([]);
  const [selectedSalesOrderId, setSelectedSalesOrderId] = useState<string>('ALL');
  const [selectedEndgoodId, setSelectedEndgoodId] = useState<string>('ALL');
  
  // Month picker date state
  const [monthPickerDate, setMonthPickerDate] = useState<Date | undefined>(
    new Date(defaultMonth.year, defaultMonth.month - 1, 1)
  );

  const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const FULL_MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  // Generate year options (last 2 years + current year + next 2 years)
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 2; i <= currentYear + 2; i++) {
      years.push(i);
    }
    return years;
  }, []);

  // Update monthPickerDate when year or month changes
  useEffect(() => {
    if (selectedYear && selectedMonth) {
      setMonthPickerDate(new Date(selectedYear, selectedMonth - 1, 1));
    }
  }, [selectedYear, selectedMonth]);


  // Load material planning data for all raw items
  const loadPlanningData = async () => {
    if (!selectedMonth) {
      setPlanningData([]);
      return;
    }

    try {
      setLoading(true);
      const branchId = selectedBranch || user?.defaultBranchId;
      
      // Fetch all planning data in a single API call
      const response = await materialPlanningApiService.getAllMaterialPlanning(
        branchId || undefined,
        selectedYear,
        selectedMonth
      );

      // Handle response - could be ApiResponse or array directly
      if (Array.isArray(response)) {
        // API returned array directly
        setPlanningData(response);
      } else if (response && response.success && Array.isArray(response.data)) {
        // API returned ApiResponse wrapper
        setPlanningData(response.data);
      } else {
        toast.error('Failed to load material planning data');
        setPlanningData([]);
      }
    } catch (error) {
      // console.error('Error loading material planning data:', error);
      toast.error('Failed to load material planning data');
      setPlanningData([]);
    } finally {
      setLoading(false);
    }
  };

  // Clear data when month/year changes (but don't auto-load)
  useEffect(() => {
    if (!selectedMonth) {
      setPlanningData([]);
    }
  }, [selectedMonth]);

  // Load data on initial mount with default month
  useEffect(() => {
    if (selectedMonth && selectedYear) {
      loadPlanningData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Format currency
  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '₹0';
    return `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format number
  const formatNumber = (value: number | null | undefined, decimals: number = 2) => {
    if (value === null || value === undefined) return '0.00';
    return value.toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  // Unique Sales Order options from loaded planning data
  const salesOrderOptions = useMemo(() => {
    const map = new Map<string, { id: string; label: string }>();
    planningData.forEach(item => {
      if (item.salesOrderId == null) return;
      const id = String(item.salesOrderId);
      if (!map.has(id)) {
        const soNumber = item.salesOrderNumber || `SO #${item.salesOrderId}`;
        const customer = item.customerName ? ` - ${item.customerName}` : '';
        map.set(id, { id, label: `${soNumber}${customer}` });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [planningData]);

  // Unique Endgood options from loaded planning data
  const endgoodOptions = useMemo(() => {
    const map = new Map<string, { id: string; label: string }>();
    planningData.forEach(item => {
      if (item.endgoodId == null) return;
      const id = String(item.endgoodId);
      if (!map.has(id)) {
        const name = item.endgoodName || `Endgood #${item.endgoodId}`;
        const sku = item.endgoodSku ? ` (${item.endgoodSku})` : '';
        map.set(id, { id, label: `${name}${sku}` });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [planningData]);

  // Reset filters to All when planning data is reloaded
  useEffect(() => {
    setSelectedSalesOrderId('ALL');
    setSelectedEndgoodId('ALL');
  }, [planningData]);

  // Apply Sales Order / Endgood filters for dashboard
  const filteredPlanningData = useMemo(() => {
    return planningData.filter(item => {
      if (selectedSalesOrderId !== 'ALL' && String(item.salesOrderId) !== selectedSalesOrderId) {
        return false;
      }
      if (selectedEndgoodId !== 'ALL' && String(item.endgoodId) !== selectedEndgoodId) {
        return false;
      }
      return true;
    });
  }, [planningData, selectedSalesOrderId, selectedEndgoodId]);

  // Group data by endgood for Finished Goods Status
  const finishedGoodsData = useMemo(() => {
    const grouped = new Map<number, {
      endgoodId: number;
      endgoodName: string;
      endgoodSku: string;
      salesOrders: Map<string, string>; // Map of salesOrderNumber -> customerName
      pendingQty: number;
      wip: number;
      packed: number;
      invoiced: number;
      inventory: number;
      shortage: number;
      status: 'Adequate' | 'High' | 'Critical';
    }>();

    filteredPlanningData.forEach(item => {
      const key = item.endgoodId;
      if (!grouped.has(key)) {
        grouped.set(key, {
          endgoodId: item.endgoodId,
          endgoodName: item.endgoodName,
          endgoodSku: item.endgoodSku,
          salesOrders: new Map(),
          pendingQty: 0,
          wip: 0,
          packed: 0,
          invoiced: 0,
          inventory: 0,
          shortage: 0,
          status: 'Adequate',
        });
      }

      const group = grouped.get(key)!;
      if (item.salesOrderNumber) {
        // Store sales order with customer name
        group.salesOrders.set(item.salesOrderNumber, item.customerName || 'N/A');
      }
      // Sum up quantities - use max to avoid double counting when same endgood appears multiple times
      group.pendingQty = Math.max(group.pendingQty, item.endgoodNetRequiredQty || 0);
      group.wip = Math.max(group.wip, item.endgoodWipQty || 0);
      group.packed = Math.max(group.packed, item.endgoodPackedQty || 0);
      group.invoiced = Math.max(group.invoiced, item.endgoodInvoicedQty || 0);
      
      // Calculate inventory: Required - Net Required (what's already available)
      const required = item.endgoodRequiredQty || 0;
      const netRequired = item.endgoodNetRequiredQty || 0;
      group.inventory = Math.max(group.inventory, Math.max(0, required - netRequired));
    });

    // Calculate shortage and status
    const result = Array.from(grouped.values()).map(group => {
      // Shortage = Pending Qty - (WIP + Inventory)
      group.shortage = Math.max(0, group.pendingQty - (group.wip + group.inventory));
      
      if (group.shortage === 0) {
        group.status = 'Adequate';
      } else if (group.shortage <= group.pendingQty * 0.3) {
        group.status = 'High';
      } else {
        group.status = 'Critical';
      }

      return {
        ...group,
        salesOrders: Array.from(group.salesOrders.entries()),
      };
    });

    return result.sort((a, b) => {
      // Sort by status priority: Critical > High > Adequate
      const statusOrder = { Critical: 0, High: 1, Adequate: 2 };
      return statusOrder[a.status] - statusOrder[b.status];
    });
  }, [filteredPlanningData]);

  // Group data by raw item for Raw Materials Required
  const rawMaterialsData = useMemo(() => {
    const grouped = new Map<number, {
      rawItemId: number;
      rawItemName: string;
      rawItemSku: string;
      inventory: number;
      inTransit: number;
      pendingPO: number;
      otherPlants: number;
      totalAvailable: number;
      required: number;
      projectedConsumption: number;
      supplierDelivery: number;
      netRequirement: number;
      shortage: number;
      status: 'Adequate' | 'Short';
      coverage: number; // days
    }>();

    filteredPlanningData.forEach(item => {
      const key = item.rawItemId;
      if (!grouped.has(key)) {
        grouped.set(key, {
          rawItemId: item.rawItemId,
          rawItemName: item.rawItemName,
          rawItemSku: item.rawItemSku,
          inventory: 0,
          inTransit: 0, // Not in current data structure - set to 0
          pendingPO: item.supplierDelivery || 0, // Using supplierDelivery as pending PO
          otherPlants: 0, // Not in current data structure - set to 0
          totalAvailable: 0,
          required: 0,
          projectedConsumption: 0,
          supplierDelivery: 0,
          netRequirement: 0,
          shortage: 0,
          status: 'Adequate',
          coverage: 0,
        });
      }

      const group = grouped.get(key)!;
      // Use the first occurrence's values (assuming they're the same for all rows of same raw item)
      if (group.inventory === 0) {
        group.inventory = item.currentInventory || 0;
      }
      // Sum up required quantities
      group.required += item.requiredQuantity || 0;
      group.projectedConsumption += item.projectedConsumption || 0;
      group.supplierDelivery = Math.max(group.supplierDelivery, item.supplierDelivery || 0);
      group.netRequirement += item.netRequirement || 0;
      // Use supplierDelivery as pending PO
      group.pendingPO = Math.max(group.pendingPO, item.supplierDelivery || 0);
    });

    // Calculate totals and status
    const result = Array.from(grouped.values()).map(group => {
      group.totalAvailable = group.inventory + group.inTransit + group.pendingPO + group.otherPlants;
      group.shortage = Math.max(0, group.required - group.totalAvailable);
      group.status = group.shortage > 0 ? 'Short' : 'Adequate';
      
      // Calculate coverage days (based on daily consumption from projected consumption)
      // Assuming 30 days in month for calculation
      const dailyConsumption = group.projectedConsumption / 30;
      group.coverage = dailyConsumption > 0 ? Math.floor(group.totalAvailable / dailyConsumption) : 
                      (group.required > 0 ? Math.floor((group.totalAvailable / group.required) * 30) : 0);

      return group;
    });

    return result.sort((a, b) => {
      // Sort by status priority: Short > Adequate
      if (a.status !== b.status) {
        return a.status === 'Short' ? -1 : 1;
      }
      // Then by shortage amount
      return b.shortage - a.shortage;
    });
  }, [filteredPlanningData]);

  // Handle export to CSV
  const handleExport = () => {
    if (filteredPlanningData.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = [
      'Raw Item',
      'Endgood',
      'Sales Order',
      'Customer',
      'Year',
      'Month',
      'Endgood Required Qty',
      'Endgood WIP Qty',
      'Endgood Packed Qty',
      'Endgood Invoiced Qty',
      'Endgood Net Required Qty',
      'Required Quantity',
      'Current Inventory',
      'Projected Consumption',
      'Supplier Delivery',
      'Net Requirement',
      'Out of Stock'
    ];

    const csvRows = [
      headers.join(','),
      ...filteredPlanningData.map(item => [
        `"${item.rawItemName || ''}"`,
        `"${item.endgoodName || ''}"`,
        `"${item.salesOrderNumber || ''}"`,
        `"${item.customerName || ''}"`,
        item.planningYear || '',
        item.planningMonth || '',
        item.endgoodRequiredQty || 0,
        item.endgoodWipQty || 0,
        item.endgoodPackedQty || 0,
        item.endgoodInvoicedQty || 0,
        item.endgoodNetRequiredQty || 0,
        item.requiredQuantity || 0,
        item.currentInventory || 0,
        item.projectedConsumption || 0,
        item.supplierDelivery || 0,
        item.netRequirement || 0,
        item.outOfStockFlag === 1 ? 'Yes' : 'No'
      ].join(','))
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filename = `Material_Planning_${timestamp}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success(`Exported ${filteredPlanningData.length} records to CSV`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-primary-200 flex items-center justify-center">
                <FileText className="h-5 w-5 text-primary-700" />
              </div>
              <div>
                <CardTitle className="text-xl text-gray-900">Material Planning</CardTitle>
                <p className="text-gray-600 text-sm">Plan and forecast material requirements</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => loadPlanningData()}
                variant="outline"
                size="sm"
                disabled={loading || !selectedMonth}
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
              {planningData.length > 0 && (
                <Button
                  onClick={handleExport}
                  variant="outline"
                  size="sm"
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Filter toolbar */}
          <div className="mb-6 rounded-xl border border-gray-200 bg-white px-4 py-4 shadow-sm">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-end">
              {/* Select Month */}
              <div className="flex-shrink-0">
                <Label className="mb-2 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">
                  <Clock className="h-3.5 w-3.5" />
                  Select Month
                </Label>
                <div className="flex flex-wrap items-center gap-2">
                  <Select
                    value={selectedMonth.toString()}
                    onValueChange={(value) => {
                      const month = parseInt(value);
                      setSelectedMonth(month);
                      setPlanningData([]);
                    }}
                  >
                    <SelectTrigger className="h-10 w-[140px] border-gray-300 bg-white focus:border-primary-400 focus:ring-primary-400">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FULL_MONTH_NAMES.map((month, index) => (
                        <SelectItem key={index + 1} value={(index + 1).toString()}>
                          {month}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={selectedYear.toString()}
                    onValueChange={(value) => {
                      const year = parseInt(value);
                      setSelectedYear(year);
                      setPlanningData([]);
                    }}
                  >
                    <SelectTrigger className="h-10 w-[100px] border-gray-300 bg-white focus:border-primary-400 focus:ring-primary-400">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {yearOptions.map((year) => (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={loadPlanningData}
                    disabled={loading || !selectedMonth}
                    className="h-10 bg-primary-600 hover:bg-primary-700 text-white border-0"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <TrendingUp className="h-4 w-4 mr-2" />
                        Start Planning
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <div className="hidden xl:block self-stretch w-px bg-gray-200 mx-2" />

              {/* Sales Order */}
              <div className="min-w-0 flex-1 xl:max-w-[280px]">
                <Label className="mb-2 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">
                  <ShoppingCart className="h-3.5 w-3.5" />
                  Sales Order
                </Label>
                <Select
                  value={selectedSalesOrderId}
                  onValueChange={setSelectedSalesOrderId}
                  disabled={planningData.length === 0}
                >
                  <SelectTrigger className="h-10 w-full border-gray-300 bg-white focus:border-primary-400 focus:ring-primary-400">
                    <SelectValue placeholder="All sales orders" />
                  </SelectTrigger>
                  <SelectContentWithSearch searchPlaceholder="Search sales orders..." debounceMs={150}>
                    <SelectItem value="ALL">All sales orders</SelectItem>
                    {salesOrderOptions.map((so) => (
                      <SelectItem key={so.id} value={so.id}>
                        {so.label}
                      </SelectItem>
                    ))}
                  </SelectContentWithSearch>
                </Select>
              </div>

              <div className="hidden xl:block self-stretch w-px bg-gray-200 mx-2" />

              {/* Endgood */}
              <div className="min-w-0 flex-1 xl:max-w-[280px]">
                <Label className="mb-2 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">
                  <Package className="h-3.5 w-3.5" />
                  Endgood
                </Label>
                <Select
                  value={selectedEndgoodId}
                  onValueChange={setSelectedEndgoodId}
                  disabled={planningData.length === 0}
                >
                  <SelectTrigger className="h-10 w-full border-gray-300 bg-white focus:border-primary-400 focus:ring-primary-400">
                    <SelectValue placeholder="All endgoods" />
                  </SelectTrigger>
                  <SelectContentWithSearch searchPlaceholder="Search endgoods..." debounceMs={150}>
                    <SelectItem value="ALL">All endgoods</SelectItem>
                    {endgoodOptions.map((eg) => (
                      <SelectItem key={eg.id} value={eg.id}>
                        {eg.label}
                      </SelectItem>
                    ))}
                  </SelectContentWithSearch>
                </Select>
              </div>
            </div>
          </div>

          {/* Planning Dashboard */}
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-primary-600" />
              <p className="text-gray-600">Loading material planning data...</p>
            </div>
          ) : planningData.length > 0 ? (
            <div className="space-y-6">
              {filteredPlanningData.length === 0 ? (
                <div className="text-center py-12 bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-lg border border-primary-200">
                  <TrendingUp className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">No data matches the selected filters</p>
                  <p className="text-gray-400 text-sm mt-2">
                    Try selecting different Sales Order or Endgood filters
                  </p>
                </div>
              ) : (
              <>
              {/* Section 1: Raw Materials Required */}
              <Card className="border border-primary-200">
                <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                  <div className="flex items-center gap-2">
                    <div className="h-6 w-6 rounded-full bg-primary-600 text-white flex items-center justify-center text-sm font-bold">
                      1
                    </div>
                    <CardTitle className="text-lg">Raw Materials Required - What We Need to Source</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="w-12"></TableHead>
                          <TableHead>Raw Material</TableHead>
                          <TableHead className="text-right">Inventory</TableHead>
                          <TableHead className="text-right">Pending PO</TableHead>
                          <TableHead className="text-right">Total Available</TableHead>
                          <TableHead className="text-right">Required</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Coverage</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rawMaterialsData.map((item) => {
                          const StatusIcon = item.status === 'Adequate' ? CheckCircle2 : XCircle;
                          const statusColor = item.status === 'Adequate' ? 'text-green-600' : 'text-red-600';
                          const badgeColor = item.status === 'Adequate' ? 'border-green-200 text-green-700 bg-green-50' : 
                                            'border-red-200 text-red-700 bg-red-50';
                          const coverageBadgeColor = item.coverage >= 15 ? 'border-green-200 text-green-700 bg-green-50' : 
                                                    item.coverage >= 7 ? 'border-orange-200 text-orange-700 bg-orange-50' : 
                                                    'border-red-200 text-red-700 bg-red-50';
                          
                          return (
                            <TableRow key={item.rawItemId} className="hover:bg-primary-50/50">
                              <TableCell>
                                <StatusIcon className={`h-5 w-5 ${statusColor}`} />
                              </TableCell>
                              <TableCell className="font-medium">
                                <button
                                  type="button"
                                  onClick={() => window.open(`/raw-items/${item.rawItemId}?view=true`, '_blank')}
                                  className="text-left w-full"
                                  title="Open raw item"
                                >
                                  <div className="text-blue-600 hover:underline">{item.rawItemName}</div>
                                  <div className="text-xs text-gray-500">{item.rawItemSku}</div>
                                </button>
                              </TableCell>
                              <TableCell className="text-right">{formatNumber(item.inventory)}</TableCell>
                              <TableCell className="text-right text-blue-600">{formatNumber(item.pendingPO)}</TableCell>
                              <TableCell className="text-right font-medium">{formatNumber(item.totalAvailable)}</TableCell>
                              <TableCell className="text-right font-semibold">{formatNumber(item.required)}</TableCell>
                              <TableCell>
                                {item.shortage > 0 ? (
                                  <span className="text-red-600 font-semibold">Short: {formatNumber(item.shortage)}</span>
                                ) : (
                                  <span className="text-green-600">✓ Adequate</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className={coverageBadgeColor}>
                                  {item.coverage} days
                                </Badge>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
              {/* Section 2 : Finished Goods Status */}
              <Card className="border border-primary-200">
                <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                  <div className="flex items-center gap-2">
                    <div className="h-6 w-6 rounded-full bg-primary-600 text-white flex items-center justify-center text-sm font-bold">
                      2
                    </div>
                    <CardTitle className="text-lg">Finished Goods Status - What We Need to Deliver</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="w-12"></TableHead>
                          <TableHead>Finished Good</TableHead>
                          <TableHead>Sales Orders</TableHead>
                          <TableHead className="text-right">Pending Qty</TableHead>
                          <TableHead className="text-right">WIP</TableHead>
                          <TableHead className="text-right">Inventory</TableHead>
                          <TableHead className="text-right">Shortage</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {finishedGoodsData.map((item) => {
                          const StatusIcon = item.status === 'Adequate' ? CheckCircle2 : 
                                            item.status === 'High' ? AlertTriangle : XCircle;
                          const statusColor = item.status === 'Adequate' ? 'text-green-600' : 
                                             item.status === 'High' ? 'text-orange-600' : 'text-red-600';
                          const badgeColor = item.status === 'Adequate' ? 'border-green-200 text-green-700 bg-green-50' : 
                                            item.status === 'High' ? 'border-orange-200 text-orange-700 bg-orange-50' : 
                                            'border-red-200 text-red-700 bg-red-50';
                          
                          return (
                            <TableRow key={item.endgoodId} className="hover:bg-primary-50/50">
                              <TableCell>
                                <StatusIcon className={`h-5 w-5 ${statusColor}`} />
                              </TableCell>
                              <TableCell className="font-medium">
                                <div>{item.endgoodName}</div>
                                <div className="text-xs text-gray-500">{item.endgoodSku}</div>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col gap-1">
                                  {item.salesOrders.map(([soNumber, customerName], idx) => (
                                    <div key={idx} className="flex flex-col">
                                      <span className="text-sm font-medium text-blue-600">{soNumber}</span>
                                      <span className="text-xs text-gray-500">{customerName}</span>
                                    </div>
                                  ))}
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-medium">{formatNumber(item.pendingQty)}</TableCell>
                              <TableCell className="text-right">{formatNumber(item.wip)}</TableCell>
                              <TableCell className={`text-right ${item.inventory > 0 ? 'text-green-600' : ''}`}>
                                {formatNumber(item.inventory)}
                              </TableCell>
                              <TableCell className="text-right">
                                {item.shortage > 0 ? (
                                  <span className="text-red-600 font-semibold">{formatNumber(item.shortage)}</span>
                                ) : (
                                  <span className="text-green-600">✓ OK</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className={badgeColor}>
                                  {item.status}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
              </>
              )}
            </div>
          ) : selectedMonth ? (
            <div className="text-center py-12 bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-lg border border-primary-200">
              <TrendingUp className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No material planning data found</p>
              <p className="text-gray-400 text-sm mt-2">
                No data available for the selected branch and time period
              </p>
            </div>
          ) : (
            <div className="text-center py-12 bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-lg border border-primary-200">
              <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">Select a month to view material planning</p>
              <p className="text-gray-400 text-sm mt-2">
                Choose a month from the dropdown above to see planning data for all raw items
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

